import {
	DateString,
	TableView,
	TableViewNoResultConfig,
	VirtualTableViewColumnDefinition
} from "client/Components";
import { SortingFor } from "client/Data/sorting";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useUserTimezone
} from "client/Hooks";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../../DocumentName";
import { columnIdToNameMapping, GridItem } from "../../SharePoint/SharePointOverView";
import { checkIsDocumentDisabled } from "./SharePointPickerOverview";
import { SharePointColumnId } from "client/Api/Documents/SharePoint/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { ItemPickerMode } from "../ItemPicker";

export type SharePointPickerTableViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointPickerTableViewProps = {
	isBusy: boolean;
	hasMoreItems: boolean;
	onLoadMoreItems: () => void;
	sortingFor: SortingFor<GridItem>;
	multiSelection: MultiSelectableOptions | undefined;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	items: readonly GridItem[];
	onOpenFolder: (item: GridItem) => void;
	mode: ItemPickerMode;
	shouldShowLocationColumn: boolean;
	hasSearch: boolean;
	loadMoreIndicatorVersion: number;
};

export const SharePointPickerTableView = forwardRef(
	(props: SharePointPickerTableViewProps, ref: Ref<SharePointPickerTableViewHandle>): ReactElement => {
		const {
			isBusy,
			hasMoreItems,
			onLoadMoreItems,
			sortingFor,
			multiSelection,
			selectionFor,
			items,
			onOpenFolder,
			mode,
			shouldShowLocationColumn,
			hasSearch,
			loadMoreIndicatorVersion
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					tableRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const columns: readonly VirtualTableViewColumnDefinition[] = useColumnDefinitions(
			sortingFor,
			shouldShowLocationColumn
		);

		const noResultConfig = useMemo(
			(): TableViewNoResultConfig | undefined => {
				if (isBusy) {
					return undefined;
				}

				return hasSearch
					? {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					}
					: {
						icon: "folder-open",
						title: localize("_NoDocumentsToShow"),
						message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")
					};
			},
			[hasSearch, isBusy]
		);

		return (
			<>
				<TableView
					isVirtual
					hasNoBottomDelimiter
					columns={columns}
					items={items}
					height="fill"
					itemKey="uniqueKey"
					trackingId="sharepoint-picker-grid"
					multiSelectable={multiSelection}
					ref={tableRef}
					shouldHideDisabledCheckboxes
					renderAfterItems={
						<TableView.LoadMoreIndicator
							key={loadMoreIndicatorVersion}
							isLoadingData={isBusy}
							hasMoreItems={hasMoreItems}
							onRequestMoreItems={() => onLoadMoreItems()}
						/>
					}
					noResultConfig={noResultConfig}
				>
					{(x): ReactElement => (
						<TableView.Row
							selectable={selectionFor(x)}
							onExecute={() => onOpenFolder(x)}
							isDisabled={checkIsDocumentDisabled(x, mode)}
							trackingId={x.uniqueKey}
						>
							<TableView.Cell>
								<DocumentName
									name={x.name}
									documentKind={x.model.itemType}
									mode={{
										kind: DocumentNameKind.Default,
										hasBookmark: Boolean(x.model.bookmarkId),
										isInfected: x.isInfected
									}}
									onClick={
										!checkIsDocumentDisabled(x, mode)
											? () => onOpenFolder(x)
											: undefined
									}
								/>
							</TableView.Cell>
							<TableView.Cell>
								<DateString
									value={x.createdOn}
									format="RelativeTimestamp"
									timezone={userTimezone}
								/>
							</TableView.Cell>
							<TableView.Cell>
								<DateString
									value={x.modifiedOn}
									format="RelativeTimestamp"
									timezone={userTimezone}
								/>
							</TableView.Cell>
							<TableView.Cell>
								{x.createdBy}
							</TableView.Cell>
							<TableView.Cell>{x.modifiedBy}</TableView.Cell>
							<TableView.Cell>{x.extension}</TableView.Cell>
							{shouldShowLocationColumn && (
								<TableView.Cell>
									{x.location}
								</TableView.Cell>
							)}
						</TableView.Row>
					)}
				</TableView>
			</>
		);
	}
);
SharePointPickerTableView.displayName = "SharePointPickerTableView";

function useColumnDefinitions(
	sortingFor: SortingFor<GridItem>,
	shouldShowLocationColumn: boolean
): readonly VirtualTableViewColumnDefinition[] {

	return useMemo(
		(): readonly VirtualTableViewColumnDefinition[] => {
			const result: VirtualTableViewColumnDefinition[] = [
				{
					title: localize(columnIdToNameMapping[SharePointColumnId.name]),
					sortable: sortingFor("name"),
					trackingId: "name",
					width: 400
				},
				{
					title: localize(columnIdToNameMapping[SharePointColumnId.dateCreated]),
					sortable: sortingFor("createdOn"),
					trackingId: "created-on",
					width: 150
				},
				{
					title: localize(columnIdToNameMapping[SharePointColumnId.dateModified]),
					sortable: sortingFor("modifiedOn"),
					trackingId: "modified-on",
					width: 150
				},
				{
					title: localize(columnIdToNameMapping[SharePointColumnId.createdBy]),
					trackingId: "created-by",
					width: 150
				},
				{
					title: localize(columnIdToNameMapping[SharePointColumnId.modifiedBy]),
					trackingId: "modified-by",
					width: 150
				},
				{
					title: localize(columnIdToNameMapping[SharePointColumnId.extension]),
					sortable: sortingFor("extension"),
					trackingId: "extension",
					width: 150
				}
			];

			if (shouldShowLocationColumn) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.path]),
					maxWidth: 700,
					trackingId: "path",
					width: 150
				});
			}

			return result;
		},
		[sortingFor, shouldShowLocationColumn]
	);
}